import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Anchor,
  Badge,
  Breadcrumbs,
  Center,
  Container,
  Group,
  Loader,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { IconListDetails, IconSettings, IconUsers } from '@tabler/icons-react';
import { useProject } from '../../queries/projects';
import { MembersTab } from './MembersTab';
import { ProjectSettingsTab } from './ProjectSettingsTab';
import { useProjectStore } from '../../project/project-store';
import { useAuthStore } from '../../auth/auth-store';

export function ProjectDetailPage() {
  const { id = '' } = useParams();
  const { data: project, isLoading, isError } = useProject(id);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'members';
  const role = useProjectStore((s) => s.projectRole);
  const user = useAuthStore((s) => s.user);

  if (isLoading) {
    return (
      <Center h="60vh">
        <Loader />
      </Center>
    );
  }

  if (isError || !project) {
    return (
      <Container>
        <Text>Project not found.</Text>
      </Container>
    );
  }

  const isSuper = user?.system_role === 'superadmin';
  const canEdit = isSuper || role === 'owner';
  const canDelete = isSuper || role === 'owner';
  const canArchive = isSuper || role === 'owner' || role === 'moderator';

  return (
    <Container size="xl">
      <Stack gap="md">
        <Breadcrumbs>
          <Anchor component={Link} to={isSuper ? '/admin/projects' : '/'}>
            {isSuper ? 'Projects (admin)' : 'Dashboard'}
          </Anchor>
          <Text>{project.name}</Text>
        </Breadcrumbs>

        <Stack gap={0}>
          <Group gap="sm">
            <Title order={2}>{project.name}</Title>
            <Badge variant="light">{project.status}</Badge>
            {role && (
              <Badge color="pine" variant="light">
                your role: {role}
              </Badge>
            )}
          </Group>
          <Text c="dimmed" size="sm">
            {project.company_name}
            {project.description && <> &middot; {project.description}</>}
          </Text>
        </Stack>

        <Tabs value={tab} onChange={(v) => v && setSearchParams({ tab: v })}>
          <Tabs.List>
            <Tabs.Tab value="tracts" leftSection={<IconListDetails size={14} />}>
              Tracts
            </Tabs.Tab>
            <Tabs.Tab value="members" leftSection={<IconUsers size={14} />}>
              Members
            </Tabs.Tab>
            <Tabs.Tab value="settings" leftSection={<IconSettings size={14} />}>
              Settings
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="tracts" pt="md">
            <Text c="dimmed" size="sm">
              Tracts are managed on the dedicated{' '}
              <Anchor component={Link} to="/tracts">
                Tracts page
              </Anchor>
              . Pick this project in the top bar first; then use /tracts to browse and edit.
            </Text>
          </Tabs.Panel>

          <Tabs.Panel value="members" pt="md">
            <MembersTab projectId={id} />
          </Tabs.Panel>

          <Tabs.Panel value="settings" pt="md">
            <ProjectSettingsTab
              project={project}
              canEdit={canEdit}
              canDelete={canDelete}
              canArchive={canArchive}
            />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
